import type { DayMetricAcc, DayRecord } from "./buckets";
import type { HealthSection } from "./health";

// 診断スナップショット sd3 (wire形式 = QRに載る形そのもの)。
// v2 からの方針転換: days をキー付きオブジェクトから位置ベースのタプルにする。
// 14日分で同じキー名を14回繰り返す冗長を除き、CBOR等のバイナリ化なしで容量を稼ぐ。
// 列定義 (cols) を自己記述として埋め込むので、スキーマ文書なしでも解釈できる。

export const SCHEMA = "sd3";

/** 日次タプルの列定義。列追加は末尾のみ (前方互換) */
export const DAY_COLS = [
  "date",
  "taps",
  "sess",
  "err",
  "hourly",
  "byScreen",
  "searches",
  "route", // [count, sumMs, maxMs] (ms は整数丸め)
  "render",
  "sessLen", // [〜30s, 〜2m, 〜5m, 5m超]
] as const;

export type MetricTuple = [number, number, number]; // [count, sumMs, maxMs]

export type DayTuple = [
  string, // date YYYY-MM-DD
  number, // taps
  number, // sessions
  number, // errors
  number[], // hourly[24]
  Record<string, number>, // byScreen
  Record<string, number>, // searches
  MetricTuple, // route
  MetricTuple, // render
  number[], // sessLen
];

/** [HH:MM:SS, name, message, stackHead?] */
export type ErrTuple = [string, string, string] | [string, string, string, string];

export interface IdSection {
  app: string;
  hash: string; // git 短縮ハッシュ (リリース検証の主キー)
  built: string; // ビルド時刻 ISO
  boot: string; // アプリ起動時刻 ISO
  svcBoot?: string; // Windowsサービス起動時刻 ISO (取得できた場合のみ)
}

export interface SnapshotSD3 {
  s: typeof SCHEMA;
  g: string; // 生成時刻 ISO
  id: IdSection;
  h: HealthSection;
  u: { cols: string[]; days: DayTuple[] };
  e: ErrTuple[];
}

export interface CapturedError {
  t: string; // HH:MM:SS
  name: string;
  message: string;
  stackHead?: string;
}

const metricTuple = (m: DayMetricAcc): MetricTuple => [
  m.count,
  Math.round(m.sumMs),
  Math.round(m.maxMs),
];

export const dayToTuple = (d: DayRecord): DayTuple => [
  d.date,
  d.taps,
  d.sessions,
  d.errors,
  d.hourly,
  d.byScreen,
  d.searches,
  metricTuple(d.route),
  metricTuple(d.render),
  d.sessLen,
];

export const errToTuple = (e: CapturedError): ErrTuple =>
  e.stackHead !== undefined ? [e.t, e.name, e.message, e.stackHead] : [e.t, e.name, e.message];

export interface SnapshotInput {
  nowIso: string;
  id: IdSection;
  health: HealthSection;
  days: DayRecord[]; // 古い→新しい順
  errors: CapturedError[]; // 古い→新しい順
}

export const buildSnapshot = (input: SnapshotInput): SnapshotSD3 => ({
  s: SCHEMA,
  g: input.nowIso,
  id: input.id,
  h: input.health,
  u: { cols: [...DAY_COLS], days: input.days.map(dayToTuple) },
  e: input.errors.map(errToTuple),
});

// --- 受信側 (ビューア/パネル) 向けのタプル展開 ---

export interface ExpandedDay {
  date: string;
  taps: number;
  sessions: number;
  errors: number;
  hourly: number[];
  byScreen: Record<string, number>;
  searches: Record<string, number>;
  route: MetricTuple;
  render: MetricTuple;
  sessLen: number[];
}

const asNum = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const asNumArr = (v: unknown): number[] => (Array.isArray(v) ? v.map(asNum) : []);
const asRec = (v: unknown): Record<string, number> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, number>) : {};
const asMetric = (v: unknown): MetricTuple => {
  const a = asNumArr(v);
  return [a[0] ?? 0, a[1] ?? 0, a[2] ?? 0];
};

/** 未知の将来列は無視する (末尾追加互換)。壊れた値は 0/空 に落として続行 */
export const expandDay = (t: unknown): ExpandedDay | null => {
  if (!Array.isArray(t) || typeof t[0] !== "string") return null;
  return {
    date: t[0],
    taps: asNum(t[1]),
    sessions: asNum(t[2]),
    errors: asNum(t[3]),
    hourly: asNumArr(t[4]),
    byScreen: asRec(t[5]),
    searches: asRec(t[6]),
    route: asMetric(t[7]),
    render: asMetric(t[8]),
    sessLen: asNumArr(t[9]),
  };
};

export const expandError = (t: unknown): CapturedError | null => {
  if (!Array.isArray(t) || t.length < 3) return null;
  const [time, name, message, stackHead] = t as unknown[];
  if (typeof time !== "string" || typeof name !== "string" || typeof message !== "string") {
    return null;
  }
  return {
    t: time,
    name,
    message,
    ...(typeof stackHead === "string" ? { stackHead } : {}),
  };
};
